import { handleActions, combineActions } from 'redux-actions'
import { fromJS, Map as ImmutableMap, Set as ImmutableSet } from 'immutable'

import * as Actions from 'actions/contact_management'
import * as AppActions from 'actions/app'

export const initialState = fromJS({
  // Navigation and search
  selectedMainTab: 'contacts',
  selectedListTab: 'contacts',
  selectedContactTab: 'overview',
  query: '',
  contactsPage: 1,

  // Request runners
  requestRunning: false,
  addContactsToListRequestRunning: false,
  downloadFailedContactsRequestRunning: false,
  fetchContactRequestRunning: false,
  fetchEarnedMediaRequestRunning: false,
  fetchEmailEventsRequestRunning: false,
  fetchMoreContactsRequestRunning: false,
  fetchMoreDistributionListsRequestRunning: false,
  findContactRequestRunning: false,
  findDistributionListRequestRunning: false,
  importRequestRunning: false,
  removeContactsFromListRequestRunning: false,
  removeContactsFromDistributionListsRequestRunning: false,
  searchContactsRequestRunning: false,
  searchDistributionListsRequestRunning: false,
  reportErrorRequestRunning: false,
  exportContactsRequestRunning: false,
  replaceContactsRequestRunning: false,

  // Detail page routing
  contactDetailsContactId: null,
  distributionListDetailsListId: null,
  contactDetailsEditMode: false,
  contactDetailErrorReport: null,
  returnToList: false,
  returnToContact: false,

  // Contact creation
  createContactUserInput: {},
  createdContactIds: ImmutableSet(),

  // Distribution list selections
  distributionListToDelete: null,
  distributionListBulkDelete: false,
  distributionListToUpdate: null,

  // Dialog openers
  distributionListDetailsOpen: false,
  distributionListUpdateDialogOpen: false,
  distributionListExportUnavailableDialogOpen: false,
  distributionListCreateDialogOpen: false,
  distributionListDeleteDialogOpen: false,
  distributionListMergeDialogOpen: false,
  contactsRemoveFromListsDialogOpen: false,
  contactsAddDialogOpen: false,
  contactDetailsAddListDialogOpen: false,
  contactCreateDialogOpen: false,
  contactRemoveDialogOpen: false,
  contactsDeleteDialogOpen: false,
  contactReportErrorDialogOpen: false,
  contactsExportDialogOpen: false,
  importPageOpen: false,
  contactSuggestDialogOpen: false
})

const getDistributionListId = payload => {
  if (!payload) {
    return null
  }

  return payload.distributionListId || null
}

export default handleActions(
  {
    // Navigation and search: these values drive the visible tabs, search term, and table controls.
    [Actions.setQuery]: (state, { payload }) => state.set('query', payload),
    [Actions.setSelectedMainTab]: (state, { payload }) => state.set('selectedMainTab', payload),
    [Actions.setSelectedListTab]: (state, { payload }) => state.set('selectedListTab', payload),
    [Actions.setSelectedContactTab]: (state, { payload }) => state.set('selectedContactTab', payload),
    [Actions.setContactsPage]: (state, { payload }) => state.set('contactsPage', payload),
    [Actions.clearContacts]: state => state.set('contactsPage', 1),

    // Request runners: full-form mutations use the shared blocking flag in dialogs and toolbars.
    [combineActions(
      Actions.deleteContactsStart,
      Actions.createDistributionListStart,
      Actions.deleteDistributionListsStart,
      Actions.updateDistributionListStart,
      Actions.mergeDistributionListsStart,
      Actions.createContactStart,
      Actions.updateContactStart,
      Actions.resetContactStart,
      Actions.importSuggestedContactStart
    )]: state => state.set('requestRunning', true),
    [combineActions(
      Actions.deleteContactsSuccess,
      Actions.deleteContactsError,
      Actions.createDistributionListSuccess,
      Actions.createDistributionListError,
      Actions.deleteDistributionListsSuccess,
      Actions.deleteDistributionListsError,
      Actions.updateDistributionListSuccess,
      Actions.updateDistributionListError,
      Actions.mergeDistributionListsSuccess,
      Actions.mergeDistributionListsError,
      Actions.createContactSuccess,
      Actions.createContactError,
      Actions.updateContactSuccess,
      Actions.updateContactError,
      Actions.resetContactSuccess,
      Actions.resetContactError,
      Actions.importSuggestedContactSuccess,
      Actions.importSuggestedContactError
    )]: state => state.set('requestRunning', false),

    // Request runners: contact and distribution list searches have independent loading indicators.
    [combineActions(
      Actions.fetchContactsStart,
      Actions.fetchSmartSearchContactIdsStart
    )]: state => state.set('searchContactsRequestRunning', true),
    [combineActions(
      Actions.fetchContactsSuccess,
      Actions.fetchContactsError,
      Actions.fetchSmartSearchContactIdsSuccess,
      Actions.fetchSmartSearchContactIdsError
    )]: state => state.set('searchContactsRequestRunning', false),

    [combineActions(
      Actions.fetchDistributionListsStart,
      Actions.fetchSmartSearchDistributionListIdsStart
    )]: state => state.set('searchDistributionListsRequestRunning', true),
    [combineActions(
      Actions.fetchDistributionListsSuccess,
      Actions.fetchDistributionListsError,
      Actions.fetchSmartSearchDistributionListIdsSuccess,
      Actions.fetchSmartSearchDistributionListIdsError
    )]: state => state.set('searchDistributionListsRequestRunning', false),

    [Actions.fetchMoreContactsStart]: state => state
      .set('fetchMoreContactsRequestRunning', true)
      .set('searchContactsRequestRunning', false),
    [Actions.fetchMoreContactsStop]: state => state.set('fetchMoreContactsRequestRunning', false),

    [Actions.fetchMoreDistributionListsStart]: state => state
      .set('fetchMoreDistributionListsRequestRunning', true)
      .set('searchDistributionListsRequestRunning', false),
    [Actions.fetchMoreDistributionListsStop]: state => state.set('fetchMoreDistributionListsRequestRunning', false),

    // Request runners: quick lookup controls inside dialogs should not block the main list views.
    [Actions.searchContactsStart]: state => state.set('findContactRequestRunning', true),
    [Actions.searchDistributionListStart]: state => state.set('findDistributionListRequestRunning', true),
    [combineActions(
      Actions.searchContactsSuccess,
      Actions.searchContactsError
    )]: state => state.set('findContactRequestRunning', false),
    [combineActions(
      Actions.searchDistributionListSuccess,
      Actions.searchDistributionListError
    )]: state => state.set('findDistributionListRequestRunning', false),

    // Request runners: contact details are split into independently loading subpanels.
    [Actions.fetchContactStart]: state => state.set('fetchContactRequestRunning', true),
    [Actions.fetchEmailEventsStart]: state => state.set('fetchEmailEventsRequestRunning', true),
    [Actions.fetchEarnedMediaStart]: state => state.set('fetchEarnedMediaRequestRunning', true),
    [combineActions(
      Actions.fetchContactSuccess,
      Actions.fetchContactError
    )]: state => state.set('fetchContactRequestRunning', false),
    [combineActions(
      Actions.fetchEmailEventsSuccess,
      Actions.fetchEmailEventsError
    )]: state => state.set('fetchEmailEventsRequestRunning', false),
    [combineActions(
      Actions.fetchEarnedMediaSuccess,
      Actions.fetchEarnedMediaError
    )]: state => state.set('fetchEarnedMediaRequestRunning', false),

    // Request runners: report, import, export, and list-membership flows use dialog-level spinners.
    [Actions.sendErrorReportStart]: state => state.set('reportErrorRequestRunning', true),
    [combineActions(
      Actions.sendErrorReportSuccess,
      Actions.sendErrorReportError
    )]: state => state.set('reportErrorRequestRunning', false),

    [combineActions(
      Actions.addContactToListStart,
      Actions.addContactsToDistributionListStart
    )]: state => state.set('addContactsToListRequestRunning', true),
    [combineActions(
      Actions.addContactToListSuccess,
      Actions.addContactToListError,
      Actions.addContactsToDistributionListSuccess,
      Actions.addContactsToDistributionListError
    )]: state => state.set('addContactsToListRequestRunning', false),

    [Actions.removeContactsFromListStart]: state => state.set('removeContactsFromListRequestRunning', true),
    [combineActions(
      Actions.removeContactsFromListSuccess,
      Actions.removeContactsFromListError
    )]: state => state.set('removeContactsFromListRequestRunning', false),

    [Actions.removeContactsFromDistributionListsStart]: state => state.set('removeContactsFromDistributionListsRequestRunning', true),
    [combineActions(
      Actions.removeContactsFromDistributionListsSuccess,
      Actions.removeContactsFromDistributionListsError
    )]: state => state.set('removeContactsFromDistributionListsRequestRunning', false),

    [combineActions(
      Actions.importUploadFileStart,
      Actions.importSetupStart,
      Actions.importFinalizeStart
    )]: state => state.set('importRequestRunning', true),
    [combineActions(
      Actions.importUploadFileSuccess,
      Actions.importUploadFileError,
      Actions.importSetupSuccess,
      Actions.importSetupError,
      Actions.importFinalizeSuccess,
      Actions.importFinalizeError
    )]: state => state.set('importRequestRunning', false),

    [Actions.downloadImportFailedContactsStart]: state => state.set('downloadFailedContactsRequestRunning', true),
    [combineActions(
      Actions.downloadImportFailedContactsSuccess,
      Actions.downloadImportFailedContactsError
    )]: state => state.set('downloadFailedContactsRequestRunning', false),

    [Actions.exportContactsStart]: state => state.set('exportContactsRequestRunning', true),
    [combineActions(
      Actions.exportContactsSuccess,
      Actions.exportContactsError
    )]: state => state.set('exportContactsRequestRunning', false),

    [Actions.replaceContactsStart]: state => state.set('replaceContactsRequestRunning', true),
    [combineActions(
      Actions.replaceContactsSuccess,
      Actions.replaceContactsError
    )]: state => state.set('replaceContactsRequestRunning', false),

    // Distribution list dialogs: dialog state stores the selected list id or bulk mode beside the opener.
    [Actions.openDistributionListCreateDialog]: state => state.set('distributionListCreateDialogOpen', true),
    [combineActions(
      Actions.closeDistributionListCreateDialog,
      Actions.createDistributionListSuccess
    )]: state => state.set('distributionListCreateDialogOpen', false),

    [Actions.openDistributionListUpdateDialog]: (state, { payload }) => state.merge({
      distributionListUpdateDialogOpen: true,
      distributionListToUpdate: payload.distributionListId
    }),
    [combineActions(
      Actions.closeDistributionListUpdateDialog,
      Actions.updateDistributionListSuccess
    )]: state => state.merge({
      distributionListUpdateDialogOpen: false,
      distributionListToUpdate: null
    }),

    [Actions.openDistributionListDeleteDialog]: (state, { payload }) => state.merge({
      distributionListDeleteDialogOpen: true,
      distributionListToDelete: payload.distributionListId || null,
      distributionListBulkDelete: payload.bulk || false
    }),
    [combineActions(
      Actions.closeDistributionListDeleteDialog,
      Actions.deleteDistributionListsSuccess
    )]: state => state.merge({
      distributionListDeleteDialogOpen: false,
      distributionListToDelete: null,
      distributionListBulkDelete: false
    }),

    [Actions.openDistributionListMergeDialog]: state => state.set('distributionListMergeDialogOpen', true),
    [combineActions(
      Actions.closeDistributionListMergeDialog,
      Actions.mergeDistributionListsSuccess
    )]: state => state.set('distributionListMergeDialogOpen', false),

    [Actions.openDistributionListExportUnavailableDialog]: state => state
      .set('distributionListExportUnavailableDialogOpen', true)
      .set('requestRunning', false),
    [Actions.closeDistributionListExportUnavailableDialog]: state => state.set('distributionListExportUnavailableDialogOpen', false),

    // Contact dialogs: these flags back modal visibility and any data the modal needs to render.
    [Actions.openContactsDeleteDialog]: state => state.set('contactsDeleteDialogOpen', true),
    [combineActions(
      Actions.closeContactsDeleteDialog,
      Actions.deleteContactsSuccess
    )]: state => state.set('contactsDeleteDialogOpen', false),

    [Actions.openContactsExportDialog]: state => state.set('contactsExportDialogOpen', true),
    [combineActions(
      Actions.closeContactsExportDialog,
      Actions.exportContactsSuccess,
      Actions.exportContactsError
    )]: state => state.set('contactsExportDialogOpen', false),

    [Actions.openContactCreateDialog]: state => state.set('contactCreateDialogOpen', true),
    [Actions.closeContactCreateDialog]: state => state
      .set('contactCreateDialogOpen', false)
      .set('createContactUserInput', ImmutableMap()),
    [Actions.openContactSuggestDialog]: state => state.set('contactSuggestDialogOpen', true),

    [Actions.openContactsRemoveFromListsDialog]: state => state.set('contactsRemoveFromListsDialogOpen', true),
    [Actions.closeContactsRemoveFromListsDialog]: state => state.set('contactsRemoveFromListsDialogOpen', false),
    [Actions.removeContactsFromDistributionListsSuccess]: state => state.set('contactsRemoveFromListsDialogOpen', false),
    [Actions.closeContactSuggestDialog]: state => state.set('contactSuggestDialogOpen', false),
    [Actions.openContactRemoveDialog]: state => state.set('contactRemoveDialogOpen', true),
    [Actions.closeContactRemoveDialog]: state => state.set('contactRemoveDialogOpen', false),
    [Actions.removeContactsFromListSuccess]: state => state.set('contactRemoveDialogOpen', false),

    [Actions.openContactsAddDialog]: state => state.set('contactsAddDialogOpen', true),
    [Actions.closeContactsAddDialog]: state => state.set('contactsAddDialogOpen', false),
    [Actions.addContactsToDistributionListSuccess]: state => state.set('contactsAddDialogOpen', false),

    [Actions.openContactDetailsAddListDialog]: state => state.set('contactDetailsAddListDialogOpen', true),
    [Actions.closeContactDetailsAddListDialog]: state => state.set('contactDetailsAddListDialogOpen', false),
    [Actions.addContactToListSuccess]: state => state.set('contactDetailsAddListDialogOpen', false),

    [Actions.openContactReportErrorDialog]: (state, { payload }) => state
      .set('contactReportErrorDialogOpen', true)
      .set('contactDetailErrorReport', fromJS(payload)),
    [Actions.closeContactReportErrorDialog]: state => state
      .set('contactReportErrorDialogOpen', false)
      .set('contactDetailErrorReport', null),

    // Detail page routing: contact and list details can drill into each other and return cleanly.
    [Actions.setDetailPageContactId]: (state, { payload }) => state.set('contactDetailsContactId', payload),
    [Actions.setContactDetailsEditMode]: (state, { payload }) => state.set('contactDetailsEditMode', payload),

    [Actions.closeDetailPages]: state => state
      .set('contactDetailsContactId', null)
      .set('contactDetailsEditMode', false)
      .set('distributionListDetailsListId', null)
      .set('distributionListDetailsOpen', false)
      .set('importPageOpen', false)
      .set('returnToList', false)
      .set('returnToContact', false),

    [Actions.closeContactDetailsPage]: state => {
      if (state.get('returnToContact')) {
        return state.set('contactDetailsEditMode', false)
      }

      return state
        .set('contactDetailsContactId', null)
        .set('contactDetailsEditMode', false)
    },

    [Actions.returnToDistributionList]: state => state
      .set('contactDetailsContactId', null)
      .set('contactDetailsEditMode', false)
      .set('distributionListDetailsOpen', true)
      .set('returnToList', false),

    [Actions.switchToListContactDetails]: (state, { payload }) => state
      .set('returnToList', true)
      .set('returnToContact', false)
      .set('contactDetailsContactId', payload.contactId)
      .set('contactDetailsEditMode', false)
      .set('distributionListDetailsOpen', false),

    [Actions.returnToContact]: state => state
      .set('distributionListDetailsOpen', false)
      .set('distributionListDetailsListId', null)
      .set('returnToContact', false),

    [Actions.switchToContactListDetails]: (state, { payload }) => state
      .set('returnToContact', true)
      .set('returnToList', false)
      .set('contactDetailsEditMode', false)
      .set('distributionListDetailsOpen', true)
      .set('distributionListDetailsListId', payload.listId),

    [Actions.openDistributionListDetails]: (state, { payload }) => state
      .set('distributionListDetailsOpen', true)
      .set('distributionListDetailsListId', getDistributionListId(payload)),

    [Actions.openImportPage]: state => state.set('importPageOpen', true),

    // Contact creation: the create flow resets form data and tracks new ids for list highlighting.
    [Actions.setNewContactData]: (state, { payload }) => state.mergeIn(['createContactUserInput'], fromJS(payload)),
    [Actions.createContactSuccess]: (state, { payload }) => {
      let intermediateState = state

      if (payload.editAfterCreate) {
        intermediateState = intermediateState
          .set('contactDetailsContactId', payload.contact.id)
          .set('contactDetailsEditMode', true)
      }

      return intermediateState
        .set('contactCreateDialogOpen', false)
        .set('contactSuggestDialogOpen', false)
        .set('createContactUserInput', ImmutableMap())
        .update('createdContactIds', ids => ids.add(payload.contact.id))
    },
    [Actions.importSuggestedContactSuccess]: (state, { payload }) => {
      let intermediateState = state

      if (payload.editAfterCreate) {
        intermediateState = intermediateState
          .set('contactDetailsContactId', payload.contact.id)
          .set('contactDetailsEditMode', true)
      }

      return intermediateState
        .set('contactSuggestDialogOpen', false)
        .set('createContactUserInput', ImmutableMap())
        .update('createdContactIds', ids => ids.add(payload.contact.id))
    },

    // Resets: route-level resets preserve the initial ordering above for easier comparison.
    [combineActions(
      Actions.resetState,
      AppActions.resetState
    )]: () => initialState
  },
  initialState
)
